/**
 * ContactKevinModal / ContactKevinTrigger ("Contacter Kevin", 2026-09-22,
 * revu le 2026-09-23).
 *
 * Replaces the "Contacter le support" mailto blocks. Every surface mounts an
 * INLINE button (ContactKevinTrigger, or its own button styled like the ones
 * around it) that opens the shared modal; the floating pill was removed on
 * 2026-09-23 because the dialer sidebar already carried a help box in the very
 * same bottom-left corner, so both sat on top of each other.
 *
 * The modal posts to POST /api/support/requests, which opens a SERVICE CASE
 * in the Swoft CRM (docs/integrations/swoft-support-requests.md). On success
 * the modal closes and a 4 s toast confirms; on error the server's French
 * message is shown inline and the form keeps its content.
 *
 * The member never classifies their own message: the case is always opened
 * with the CRM category Inquiry, filled in by the server.
 *
 * Attachments (2026-09-23): up to MAX_ATTACHMENTS files of at most
 * MAX_ATTACHMENT_BYTES each, images or PDF. They travel as multipart/form-data
 * and the server folds them into the case description, the way the framework's
 * portal_open_ticket does.
 */

import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Paperclip, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { createSupportRequest } from '@/api/endpoints/support';
import { ApiErrorException } from '@/api/client';

const SENT_TOAST_MS = 4000;

/** At most three files, five megabytes each: the server enforces the same two numbers. */
export const MAX_ATTACHMENTS = 3;
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

/** Images and PDF only, matching the server's allow-list. */
export const ACCEPTED_ATTACHMENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
] as const;

/** What the file picker offers, extensions included for the browsers that need them. */
export const ATTACHMENT_ACCEPT = `${ACCEPTED_ATTACHMENT_TYPES.join(',')},.png,.jpg,.jpeg,.gif,.webp,.pdf`;

/** The i18n key of the refusal, or null when the selection was taken. */
export type AttachmentRefusal =
  | 'contactKevin.attachmentsTooMany'
  | 'contactKevin.attachmentsTooLarge'
  | 'contactKevin.attachmentsBadType'
  | null;

export interface AttachmentSelection {
  files: File[];
  refusal: AttachmentRefusal;
}

/**
 * Adds a picked batch to the current selection. Pure, so the rules are
 * testable without a DOM: the first refusal wins and nothing is added.
 */
export function addAttachments(current: File[], incoming: File[]): AttachmentSelection {
  if (incoming.length === 0) return { files: current, refusal: null };
  if (current.length + incoming.length > MAX_ATTACHMENTS) {
    return { files: current, refusal: 'contactKevin.attachmentsTooMany' };
  }
  for (const file of incoming) {
    if (!(ACCEPTED_ATTACHMENT_TYPES as readonly string[]).includes(file.type)) {
      return { files: current, refusal: 'contactKevin.attachmentsBadType' };
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      return { files: current, refusal: 'contactKevin.attachmentsTooLarge' };
    }
  }
  return { files: [...current, ...incoming], refusal: null };
}

/** Drops the file at `index`; an index nobody selected leaves the list alone. */
export function removeAttachment(current: File[], index: number): File[] {
  if (index < 0 || index >= current.length) return current;
  return current.filter((_, i) => i !== index);
}

/** "1,4 Mo" : the size as the member reads it under the file name. */
export function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
}

/** The current page, for the case header; no router hook so the modal works from anywhere. */
function currentPage(): string {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

export function ContactKevinModal({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation('dialer');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // A fresh form every time the modal opens.
  useEffect(() => {
    if (!open) return;
    setSubject('');
    setMessage('');
    setFiles([]);
    setError(null);
    setSending(false);
  }, [open]);

  const canSend = subject.trim().length > 0 && message.trim().length > 0 && !sending;

  function onFilesPicked(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    const next = addAttachments(files, picked);
    setFiles(next.files);
    setError(next.refusal ? t(next.refusal, { max: MAX_ATTACHMENTS, size: '5 Mo' }) : null);
    // Always clear the input, otherwise picking the same file twice is silent.
    e.target.value = '';
  }

  async function send(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      await createSupportRequest({ subject: subject.trim(), message: message.trim(), page: currentPage(), files });
      onOpenChange(false);
      toast.success(t('contactKevin.sent'), { duration: SENT_TOAST_MS });
    } catch (err) {
      // 400, 502 and 503 carry a French message written for the member;
      // anything else (network, unexpected) gets the generic one.
      const carriesFrench = err instanceof ApiErrorException && [400, 413, 502, 503].includes(err.status);
      setError((carriesFrench ? (err as ApiErrorException).message : null) || t('contactKevin.error'));
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!sending) onOpenChange(next); }}>
      <DialogContent className="sm:max-w-md" data-testid="contact-kevin-modal">
        <form onSubmit={send} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('contactKevin.title')}</DialogTitle>
            <DialogDescription>{t('contactKevin.helpText')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="contact-kevin-subject">{t('contactKevin.subject')}</Label>
            <Input
              id="contact-kevin-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('contactKevin.subjectPlaceholder')}
              maxLength={200}
              autoFocus
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-kevin-message">{t('contactKevin.message')}</Label>
            <Textarea
              id="contact-kevin-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('contactKevin.messagePlaceholder')}
              rows={5}
              maxLength={5000}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-kevin-files">{t('contactKevin.attachments')}</Label>
            <input
              id="contact-kevin-files"
              ref={fileInput}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              onChange={onFilesPicked}
              className="sr-only"
              data-testid="contact-kevin-file-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => fileInput.current?.click()}
              disabled={sending || files.length >= MAX_ATTACHMENTS}
              data-testid="contact-kevin-add-file"
            >
              <Paperclip className="h-4 w-4" />
              {t('contactKevin.attachmentsAdd')}
            </Button>
            <p className="text-xs text-muted-foreground">
              {t('contactKevin.attachmentsHint', { max: MAX_ATTACHMENTS, size: '5 Mo' })}
            </p>
            {files.length > 0 && (
              <ul className="space-y-1" data-testid="contact-kevin-file-list">
                {files.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between gap-2 rounded-md bg-muted px-2 py-1 text-xs"
                  >
                    <span className="truncate">
                      {file.name} <span className="text-muted-foreground">({formatAttachmentSize(file.size)})</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setFiles((current) => removeAttachment(current, index))}
                      disabled={sending}
                      aria-label={t('contactKevin.attachmentsRemove', { name: file.name })}
                      data-testid={`contact-kevin-remove-file-${index}`}
                      className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && (
            <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="contact-kevin-error">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={sending}>
              {t('contactKevin.cancel')}
            </Button>
            <Button type="submit" disabled={!canSend}>
              {sending ? t('contactKevin.sending') : t('contactKevin.send')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export interface ContactKevinTriggerProps {
  /** Matches the buttons around it; the surfaces that have their own style pass their own button. */
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  size?: 'default' | 'sm' | 'lg';
  className?: string;
}

/**
 * Plain inline button, for the surfaces that had no in-page way to reach
 * Kevin (the dialer gate screens, the super admin header, the backoffice
 * sidebar). It carries its own modal so a surface only has to drop it in.
 */
export function ContactKevinTrigger({ variant = 'outline', size = 'sm', className }: ContactKevinTriggerProps) {
  const { t } = useTranslation('dialer');
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        onClick={() => setOpen(true)}
        data-testid="contact-kevin-trigger"
      >
        {t('contactKevin.button')}
      </Button>
      <ContactKevinModal open={open} onOpenChange={setOpen} />
    </>
  );
}
